import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import URLS from 'URLS';
import { useTranslation } from 'react-i18next';
import { useIsSchoolAuth } from 'hooks/Auth';
import { useAdmin } from 'apps/AdminPanel/hooks/useAdmin';

// Material UI Components
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import MuiListItem from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// Assets/Icons
import LOGO from 'assets/icons/ECPlayLogo.svg';
import MenuIcon from '@mui/icons-material/MenuRounded';
import HomeIcon from '@mui/icons-material/HomeRounded';

type SideBarProps = {
  pages: Array<{
    link: string;
    text: string;
    external?: boolean;
    icon: ReactNode;
  }>;
};

const SideBar = ({ pages }: SideBarProps) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const toggleDrawer = () => setOpen((prev) => !prev);

  type ListItemProps = {
    icon: ReactNode;
    text: string;
    to: string;
    external?: boolean;
  };

  const ListItem = ({ to, icon, text, external }: ListItemProps) => (
    <MuiListItem
      onClick={() => {
        if (external) {
          window.open(to);
        } else {
          navigate(to);
        }
      }}
      selected={location.pathname === to}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={text} />
    </MuiListItem>
  );

  return (
    <>
      <IconButton aria-label='Toggle sidebar' onClick={toggleDrawer}>
        <MenuIcon sx={{ fill: (theme) => theme.palette.white?.main || '#fff' }} />
      </IconButton>
      <Drawer anchor='left' onClose={toggleDrawer} open={open} slotProps={{ paper: { sx: { minWidth: 275, width: '70%', maxWidth: 400 } } }}>
        <List>
          <Box alt='EC-Play logo' component='img' src={LOGO} sx={{ m: '8px 16px', width: 150, height: 'auto' }} />
          <ListItem icon={<HomeIcon />} text={t('APPS.HOME')} to={URLS.LANDING} />
          {pages.map((page) => (
            <ListItem external={page.external} icon={page.icon} key={page.link} text={page.text} to={page.link} />
          ))}
        </List>
        <Divider />
      </Drawer>
    </>
  );
};

export default SideBar;
